// State factory for the shared "Manage Products" modal (Opportunity, Order,
// Contract, Proposal). Exported separately from the Alpine registration so the
// pure state logic can be unit-tested without a browser. See
// app/views/shared/_products_modal_body.html.erb for the markup.
export function productsModalData(products, initialItems, currency, inheritLabel) {
  return {
    products: products,
    items: initialItems,
    currency: currency,
    inheritLabel: inheritLabel,
    search: "",

    productFor(item) {
      return this.products.find((p) => p.id === item.product_id) || null;
    },

    inheritOptionLabel(item) {
      const product = this.productFor(item);
      const name = product && product.revenue_category_name;
      return name ? `${this.inheritLabel} (${name})` : this.inheritLabel;
    },

    get filteredProducts() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.products;
      return this.products.filter((p) => {
        // Variant SKUs count: a product group's own SKU is only a label, and
        // people search for the SKU their accounting system shows them.
        const skus = [p.sku || "", ...(p.variant_skus || [])].join(" ").toLowerCase();
        return (p.name || "").toLowerCase().includes(q) || skus.includes(q);
      });
    },

    get visibleItems() {
      return this.items.filter((i) => !i._destroy);
    },

    get subtotalCents() {
      return this.items.reduce((sum, i) => (i._destroy ? sum : sum + this.lineTotalCents(i)), 0);
    },

    // Mirrors LineItem#compute_line_totals: usage lines bill afterwards, and a
    // discount stops at the line's gross.
    lineTotalCents(item) {
      if (item.billing_type === "usage") return 0;

      const qty = parseFloat(item.quantity) || 0;
      const priceCents = Math.round((parseFloat(item.unit_price) || 0) * 100);
      const gross = qty * priceCents;
      return gross - this.discountCents(item, gross);
    },

    discountCents(item, gross) {
      if (gross <= 0) return 0;

      const value = parseFloat(this.discountValue(item));
      if (Number.isNaN(value)) return 0;

      const cents = item.discount_type === "amount" ? value * 100 : (gross * value) / 100;
      return Math.min(Math.round(cents), gross);
    },

    // The form holds one value and a type, the line stores each in its own field.
    discountType(item) {
      return item.discount_type || "percentage";
    },

    discountField(item) {
      return this.discountType(item) === "amount" ? "discount_amount" : "discount_percentage";
    },

    discountValue(item) {
      return item[this.discountField(item)] ?? "";
    },

    setDiscountValue(item, value) {
      item.discount_type = this.discountType(item);
      item[this.discountField(item)] = value;
    },

    setDiscountType(item, type) {
      const value = this.discountValue(item);
      item[this.discountField(item)] = null;
      item.discount_type = type;
      item[this.discountField(item)] = value;
    },

    discountTypeParam(item) {
      return this.discountValue(item) === "" ? "" : this.discountType(item);
    },

    // Hourly and usage lines price from their rate, which the price field edits.
    priceParam(item) {
      return { hourly: "hourly_rate", usage: "overage_rate" }[item.billing_type] || "unit_price";
    },

    catalogPriceCents(product) {
      // An hourly product may be priced only in its default price. A usage line bills
      // through its rate alone, so a default price says nothing about it.
      if (product.billing_type === "usage") return product.overage_rate_cents || 0;
      if (product.billing_type === "hourly") {
        return product.hourly_rate_cents || product.default_price_cents || 0;
      }
      return product.default_price_cents || 0;
    },

    labelFor(template, item) {
      return template.replace("%{name}", () => item.name);
    },

    addProduct(product) {
      // Always append a new line so the same product can appear on multiple
      // line items, each with its own price, revenue category, VAT and
      // description. Use the per-line quantity stepper to bump a single line.
      this.items.push({
        key: "new-" + Date.now() + "-" + Math.random().toString(36).slice(2, 6),
        id: null,
        product_id: product.id,
        name: product.name,
        quantity: 1,
        unit_price: this.catalogPriceCents(product) / 100,
        revenue_category_id: "",
        billing_type: product.billing_type,
        _destroy: false
      });
    },

    increment(idx) {
      this.items[idx].quantity = (parseFloat(this.items[idx].quantity) || 0) + 1;
    },

    decrement(idx) {
      const q = (parseFloat(this.items[idx].quantity) || 0) - 1;
      this.items[idx].quantity = q < 0 ? 0 : q;
    },

    removeItem(idx) {
      const item = this.items[idx];
      if (item.id) {
        item._destroy = true;
      } else {
        this.items.splice(idx, 1);
      }
    },

    formatMoney(cents) {
      const value = (cents || 0) / 100;
      return value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
  };
}

export default function registerProductsModal(Alpine) {
  Alpine.data("productsModal", productsModalData);
}
